import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowTrendingUpIcon,
  ChevronRightIcon,
  MapIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { useInterestMap } from "../../hooks/useInterestMap";
import useTranslation from "../../hooks/useTranslation";

export const Route = createFileRoute("/app/interest_map")({
  component: InterestMapPage,
});

const INTEREST_MAP_PREFILL_STORAGE_KEY = "interest-map-prefill-topic";

function InterestMapPage() {
  const interestMap = useInterestMap();
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedKey && interestMap.data?.areas?.length) {
      setSelectedKey(interestMap.data.areas[0].key);
    }
  }, [interestMap.data?.areas, selectedKey]);

  const selectedArea = useMemo(
    () =>
      interestMap.data?.areas.find((area) => area.key === selectedKey) ??
      interestMap.data?.areas[0],
    [interestMap.data?.areas, selectedKey],
  );

  const handleUseTopic = (topic: string) => {
    sessionStorage.setItem(INTEREST_MAP_PREFILL_STORAGE_KEY, topic);
    navigate({ to: "/app/reeprise" });
  };

  if (interestMap.isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6">
        <section className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,#eef4fb_0%,#f4f8fc_65%,#f8fafc_100%)] px-5 py-5 sm:px-6">
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_14%_18%,rgba(255,255,255,0.95),transparent_24%),radial-gradient(circle_at_86%_14%,rgba(219,234,254,0.75),transparent_22%)]" />
          <p className="relative text-sm text-slate-600">Loading...</p>
        </section>
      </div>
    );
  }

  if (!interestMap.data) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6">
        <section className="rounded-[28px] border border-slate-200 bg-slate-50 px-5 py-5 sm:px-6">
          <p className="text-base text-slate-600">
            {translate("interest_map_error")}
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6">
      <section className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,#eef4fb_0%,#f4f8fc_65%,#f8fafc_100%)]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-8 top-6 h-20 w-20 rounded-full border border-white/80 bg-white/70" />
          <div className="absolute left-12 top-12 h-12 w-12 rounded-full border border-white/70 bg-white/60" />
          <div className="absolute right-10 top-8 h-16 w-16 rounded-full border border-sky-100/80 bg-sky-50/70" />
          <div className="absolute right-24 top-16 h-10 w-10 rounded-full border border-white/70 bg-white/55" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(255,255,255,0.95),transparent_24%),radial-gradient(circle_at_84%_12%,rgba(219,234,254,0.8),transparent_22%),linear-gradient(180deg,rgba(255,255,255,0.2),transparent_45%)]" />
        </div>

        <div className="relative border-b border-slate-200 px-5 py-5 sm:px-6">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-slate-700">
              <MapIcon className="h-3.5 w-3.5" />
              {translate("interest_map_eyebrow")}
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="min-w-0">
              <h1 className="font-serif text-3xl leading-tight text-slate-900 sm:text-4xl">
                {translate("interest_map_title")}
              </h1>
              <p className="mt-2 max-w-2xl text-base leading-relaxed text-slate-700">
                {translate("interest_map_subtitle")}
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <InsightBadge
                label={translate("interest_map_dominant")}
                value={translateCategory(
                  translate,
                  interestMap.data.overview.dominant_cluster_key,
                )}
              />
              <InsightBadge
                label={translate("interest_map_growth")}
                value={translateCategory(
                  translate,
                  interestMap.data.overview.fastest_growing_cluster_key,
                )}
              />
              <InsightBadge
                label={translate("interest_map_blind_spot")}
                value={translateCategory(
                  translate,
                  interestMap.data.overview.blind_spot_cluster_key,
                )}
              />
            </div>
          </div>
        </div>

        {interestMap.data.areas.length === 0 ? (
          <div className="px-5 py-6 sm:px-6">
            <div className="rounded-[24px] border border-slate-200 bg-white/70 px-5 py-8 text-center">
              <MapIcon className="mx-auto h-8 w-8 text-slate-400" />
              <p className="mt-4 font-serif text-2xl text-slate-900">
                {translate("interest_map_empty_title")}
              </p>
              <p className="mx-auto mt-2 max-w-md text-base leading-relaxed text-slate-600">
                {translate("interest_map_empty_body")}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 px-5 py-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] sm:px-6">
            <section className="rounded-[24px] border border-slate-200 bg-white/70">
              <div className="border-b border-slate-200 px-4 py-4 sm:px-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      {translate("interest_map_canvas_label")}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      {translate("interest_map_canvas_hint")}
                    </p>
                  </div>
                  <SparklesIcon className="mt-0.5 h-5 w-5 text-sky-700" />
                </div>
              </div>
              <div className="p-4 sm:p-5">
                <div className="relative min-h-[360px] overflow-hidden rounded-[22px] border border-slate-200 bg-[linear-gradient(180deg,#f8fbfe_0%,#f3f7fa_100%)]">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(255,255,255,0.9),transparent_22%),radial-gradient(circle_at_82%_18%,rgba(191,219,254,0.55),transparent_24%),radial-gradient(circle_at_50%_84%,rgba(226,232,240,0.55),transparent_28%)]" />
                  {interestMap.data.areas.map((area, index) => {
                    const position = blobPosition(index, interestMap.data.areas.length);
                    const isSelected = selectedArea?.key === area.key;
                    const size = 104 + Math.round(area.strength_score * 84);
                    return (
                      <button
                        key={area.key}
                        type="button"
                        onClick={() => setSelectedKey(area.key)}
                        className={`absolute flex items-center justify-center rounded-[36%_64%_58%_42%/48%_40%_60%_52%] border px-3 text-center transition-colors ${
                          isSelected
                            ? "border-slate-500 bg-[linear-gradient(180deg,#ffffff_0%,#eef5fb_100%)]"
                            : "border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f7fafc_100%)] hover:border-slate-300"
                        }`}
                        style={{
                          left: `${position.x}%`,
                          top: `${position.y}%`,
                          width: `${size}px`,
                          height: `${Math.round(size * 0.8)}px`,
                          transform: "translate(-50%, -50%)",
                        }}
                      >
                        <div>
                          <p className="text-sm font-semibold leading-tight text-slate-900">
                            {translateCategory(translate, area.key)}
                          </p>
                          <p className="mt-1 text-[10px] uppercase tracking-[0.16em] text-slate-500">
                            {Math.round(area.strength_score * 100)}%
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="space-y-4">
              {selectedArea ? (
                <div className="rounded-[24px] border border-slate-200 bg-white/70">
                  <div className="border-b border-slate-200 px-4 py-4 sm:px-5">
                    <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      {translate("interest_map_selected_label")}
                    </p>
                    <h2 className="mt-2 font-serif text-3xl text-slate-900">
                      {translateCategory(translate, selectedArea.key)}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      {translate("interest_map_selected_body")}
                    </p>
                  </div>
                  <div className="grid gap-2 p-4 sm:grid-cols-2 sm:p-5">
                    <MetricTile
                      label={translate("interest_map_metric_exposure")}
                      value={`${selectedArea.signal_count}`}
                      tone="neutral"
                    />
                    <MetricTile
                      label={translate("interest_map_metric_vocab")}
                      value={`${selectedArea.vocabulary_count}`}
                      tone="sky"
                    />
                    <MetricTile
                      label={translate("interest_map_metric_retention")}
                      value={`${Math.round(selectedArea.retention_score * 100)}%`}
                      tone="sky"
                    />
                    <MetricTile
                      label={translate("interest_map_metric_growth")}
                      value={`${Math.round(selectedArea.growth_score * 100)}%`}
                      tone="neutral"
                    />
                  </div>
                </div>
              ) : null}

              <div className="rounded-[24px] border border-slate-200 bg-white/70">
                <div className="border-b border-slate-200 px-4 py-4 sm:px-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                        {translate("interest_map_recommendations_label")}
                      </p>
                      <h2 className="mt-2 font-serif text-2xl text-slate-900">
                        {translate("interest_map_recommendations_title")}
                      </h2>
                    </div>
                    <ArrowTrendingUpIcon className="mt-0.5 h-5 w-5 text-slate-700" />
                  </div>
                </div>
                <div className="space-y-3 p-4 sm:p-5">
                  {interestMap.data.recommendations.map((recommendation) => (
                    <div
                      key={`${recommendation.cluster_key}-${recommendation.suggested_topic}`}
                      className="rounded-[20px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4"
                    >
                      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
                        {translate("interest_map_recommendation_for")}{" "}
                        {translateCategory(translate, recommendation.cluster_key)}
                      </p>
                      <p className="mt-2 font-serif text-xl leading-snug text-slate-900">
                        {recommendation.suggested_topic}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleUseTopic(recommendation.suggested_topic)}
                        className="mt-4 inline-flex items-center gap-1 border-b border-slate-300 pb-1 text-[11px] uppercase tracking-[0.16em] text-slate-700 transition-colors hover:text-slate-900"
                      >
                        {translate("interest_map_use_topic")}
                        <ChevronRightIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}
      </section>
    </div>
  );
}

function InsightBadge(props: { label: string; value: string }) {
  return (
    <div className="rounded-[18px] border border-slate-200 bg-white/75 px-3 py-3">
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
        {props.label}
      </p>
      <p className="mt-1 text-sm font-semibold leading-snug text-slate-900">
        {props.value}
      </p>
    </div>
  );
}

function MetricTile(props: {
  label: string;
  value: string;
  tone: "neutral" | "sky";
}) {
  const toneClass =
    props.tone === "sky"
      ? "bg-[linear-gradient(180deg,#f2f8fd_0%,#f8fbfe_100%)]"
      : "bg-[linear-gradient(180deg,#fbfcfd_0%,#f7f9fb_100%)]";

  return (
    <div className={`rounded-[18px] border border-slate-200 px-3 py-3 ${toneClass}`}>
      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
        {props.label}
      </p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{props.value}</p>
    </div>
  );
}

function translateCategory(
  translate: (key: string) => string,
  categoryKey: string | null,
) {
  if (!categoryKey) {
    return translate("interest_map_unknown_category");
  }
  return translate(`interest_map.category.${categoryKey}`);
}

function blobPosition(index: number, total: number) {
  if (total <= 1) {
    return { x: 50, y: 50 };
  }
  const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
  const radius = index % 2 === 0 ? 26 : 18;
  return {
    x: 50 + Math.cos(angle) * radius,
    y: 50 + Math.sin(angle) * (radius * 0.82),
  };
}
